/**
 * The Extraction pane's "Extracted details" rows (#349).
 *
 * These rows were read-only display, and correcting one of the values they
 * showed meant leaving them for a differently-shaped block further down the
 * drawer — so between reading a digit off the card and typing it, the card was
 * gone and the value was typed from memory. That is the exact failure the
 * zoom-to-100% work existed to delete. The rows are now the editing surface.
 *
 * Editability is a HOVER state, not a permanent control: a faint row fill and a
 * pencil at the right edge, invisible at rest, so a pane where nothing is wrong
 * still reads as a list of facts rather than a form. The row becomes an input
 * only once it is activated — which is also why the value keeps its typography
 * at rest instead of sitting in a box.
 *
 * The draft lives in the shared controller (`useCardCorrection`), the SAME one
 * the full-screen viewer renders, so a correction typed here and one typed
 * beside the blown-up card are one edit with one save.
 */
import { useCallback, useRef, useState } from 'react'
import { cardFieldLabel, type CardFieldRead } from '../utils/cardReadState'
import { fmtDob } from '../utils/smsThread'
import type { CardDetailRow } from '../utils/cardDetailRows'
import type { CorrectableField } from '../utils/checkCorrection'
import type { CardCorrection } from '../hooks/useCardCorrection'
import { IconSparkle } from './Icons'
import DateInput from './DateInput'
import PayerSearch from './PayerSearch'

/** Labels for the two inputs a Cardholder row splits into. */
const NAME_LABEL: Partial<Record<CorrectableField, string>> = {
  firstName: 'First name',
  lastName: 'Last name',
}

/** #309's three kinds of "no value" read differently, and the pane was
 *  flattening them into one amber italic. An unread value is a GAP, not an
 *  alarm — grey — and only a card that was printed-but-illegible is the darker,
 *  italic one that asks for a second look. */
function valueModifier(state: CardFieldRead): string {
  switch (state.kind) {
    case 'read': return ''
    case 'unreadable': return ' rl-details__value--illegible'
    default: return ' rl-details__value--absent'
  }
}

function Pencil() {
  return (
    <svg className="rl-details__pencil" width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3z"
        stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"
      />
    </svg>
  )
}

/** The markers that ride alongside a value on both the static and active row.
 *  Both describe the CARD's value — "the re-check runs with the card's payer",
 *  "the patient texted this" — so neither may sit on a value the operator has
 *  typed over it: an edited row shows the operator's text, not the card's. */
function RowMarks({ row, edited }: { row: CardDetailRow; edited: boolean }) {
  if (edited || row.state.kind !== 'read') return null
  return (
    <>
      {row.ai && (
        <span
          className="rl-details__ai"
          title="The card names a different insurer than the one on file, so the re-check runs with the card's payer and member ID."
        >
          <IconSparkle /> Suggested by VitalRev AI
        </span>
      )}
      {/* A texted answer is merged into the read with found=true and confidence
          1, indistinguishable from a clean OCR hit — this is the only thing
          separating "the card says so" from "the patient told us so". */}
      {row.stated && (
        <span
          className="rl-details__stated"
          title="The patient sent this in a text message — it was not read off the card."
        >
          from a text
        </span>
      )}
    </>
  )
}

function DetailRow({
  row, correction, active, onActivate, onDeactivate,
}: {
  row: CardDetailRow
  correction: CardCorrection | null
  active: boolean
  onActivate: () => void
  onDeactivate: () => void
}) {
  // `ready` is load-bearing: until the host has settled WHICH check these rows
  // describe, a keystroke would freeze the not-yet-current values into the
  // draft, and once the real ones landed every untouched field read as an edit
  // — sent on Save, it would have put the OLD values back. No pencil until
  // the values are the ones being asked.
  const editable = !!correction && correction.canWrite && correction.ready && !correction.saving
  const edited = !!correction && row.fields.some(correction.isEdited)
  const was = correction
    ? row.fields.map(correction.originalOf).filter(Boolean).join(' ')
    : ''
  const rowRef = useRef<HTMLDivElement>(null)

  // Close when focus leaves the ROW, not the input: the payer control's results
  // are buttons inside this row, and closing on the input's own blur would tear
  // the editor down before the click on a result ever landed.
  const onBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null
    if (next && rowRef.current?.contains(next)) return
    onDeactivate()
  }, [onDeactivate])

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Enter on a payer SUGGESTION is a pick, not "done". The suggestions are
    // buttons inside this row, and cancelling their keydown cancels the click
    // the browser fires for it — while closing the row would unmount the
    // picker before that click could run. Only Enter in a text field leaves.
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'BUTTON') return
    // Escape leaves the row; the typed value stays in the draft, exactly like
    // clicking away. Discarding here would be a second, invisible discard path.
    if (e.key === 'Escape' || e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      onDeactivate()
    }
  }, [onDeactivate])

  /**
   * What the row SHOWS. The draft wins wherever it holds a value.
   *
   * This has to hold WHILE A SAVE IS IN FLIGHT, which is the entire duration of
   * the re-check: `editable` goes false the moment `saving` does, and the row
   * used to fall through to the read-only branch — which renders the card/check
   * state and knows nothing about what was typed. So the screen showed the
   * value the operator had just corrected away from, for as long as the new
   * answer took to arrive, and only caught up once the callable returned.
   */
  const drafted = correction
    ? row.fields
      .map((f) => {
        const v = correction.values[f].trim()
        // The draft stores dates as ISO for the date control; the row reads
        // like the card.
        return v && f === 'dateOfBirth' ? fmtDob(v) : v
      })
      .filter(Boolean)
      .join(' ')
    : ''
  const displayed = drafted || cardFieldLabel(row.state)
  // A typed value is a real value, whatever the card managed to read.
  const valueClass = `rl-details__value${drafted ? '' : valueModifier(row.state)}`

  return (
    <div
      ref={rowRef}
      className={`rl-details__row${active ? ' rl-details__row--active' : ''}${editable ? ' rl-details__row--editable' : ''}`}
      title={was ? `Was ${was}` : undefined}
      onBlur={active ? onBlur : undefined}
      onKeyDown={active ? onKeyDown : undefined}
    >
      <span className="rl-details__label">
        {edited && <span className="rl-details__dot" aria-hidden="true" />}
        <span>{row.label}</span>
      </span>

      {active && correction ? (
        <span className="rl-details__control">
          <RowControl row={row} correction={correction} />
        </span>
      ) : editable ? (
        <button
          type="button"
          className="rl-details__open"
          onClick={onActivate}
          // The row already names the field; this is what a screen reader hears
          // in place of the bare value, and it has to say the value is editable.
          aria-label={`${row.label}: ${displayed}. Edit`}
        >
          <span className={valueClass}>{displayed}</span>
          <RowMarks row={row} edited={edited} />
          <Pencil />
        </button>
      ) : (
        <>
          <span className={valueClass}>{displayed}</span>
          <RowMarks row={row} edited={edited} />
        </>
      )}
    </div>
  )
}

function RowControl({ row, correction }: { row: CardDetailRow; correction: CardCorrection }) {
  const { values, setValue, setPayer, saving, canWrite } = correction
  const disabled = !canWrite || saving
  const placeholder = row.state.kind === 'read' ? undefined : cardFieldLabel(row.state)
  const id = `rl-details-edit-${row.fields.join('-')}`

  if (row.fields[0] === 'payerName') {
    return (
      <PayerSearch
        inputId={id}
        value={values.payerName}
        disabled={disabled}
        hideLabel
        // Like the other three rows: the opener that held focus has just
        // unmounted, and the row's Escape / click-away handling only works
        // with focus inside it.
        autoFocus
        placeholder={placeholder ?? 'Search payers…'}
        inputClassName="rl-details__input"
        // The PICK, ids and all — a human-chosen payer is authoritative on
        // the backend, never re-resolved through a clinic alias (#349).
        // No `onClear`: typing only explores the directory, and the draft
        // moves on a pick alone. Blanking the draft on every keystroke (the
        // check editor's convention) silently threw away a payer already
        // picked — type one letter, press Escape, and the row was back on the
        // old payer with no dot. Here the input unmounts on the way out, so
        // the blank was never even visible.
        onSelect={(p) => setPayer(p)}
      />
    )
  }
  if (row.fields[0] === 'dateOfBirth') {
    return (
      <DateInput
        id={id}
        className="rl-details__input"
        value={values.dateOfBirth}
        disabled={disabled}
        autoFocus
        onValueChange={(v) => setValue('dateOfBirth', v)}
      />
    )
  }
  if (row.fields.length > 1) {
    return (
      <span className="rl-details__pair">
        {row.fields.map((f, i) => (
          <input
            key={f}
            id={i === 0 ? id : undefined}
            className="rl-details__input"
            value={values[f]}
            disabled={disabled}
            autoFocus={i === 0}
            placeholder={NAME_LABEL[f]}
            aria-label={NAME_LABEL[f]}
            onChange={(e) => setValue(f, e.target.value)}
          />
        ))}
      </span>
    )
  }
  const field = row.fields[0]
  return (
    <input
      id={id}
      className={`rl-details__input${field === 'memberId' ? ' rl-details__input--mono' : ''}`}
      value={values[field]}
      disabled={disabled}
      autoFocus
      placeholder={placeholder}
      onChange={(e) => setValue(field, e.target.value)}
    />
  )
}

export interface CardDetailRowsProps {
  rows: CardDetailRow[]
  /** Present ⇒ the rows are editable. Absent ⇒ the read-only list (the staff
   *  console, which is cross-org with no org to correct a check in). */
  correction?: CardCorrection | null
}

export default function CardDetailRows({ rows, correction = null }: CardDetailRowsProps) {
  const [activeLabel, setActiveLabel] = useState<string | null>(null)
  // No "unsaved edits" bar here on purpose: the drawer already reports this
  // draft's dirtiness through `useUnsavedGuard` and renders ONE warning for
  // every kind of typed work it holds (a correction, an unsent patient
  // message). A second bar would be a second thing to dismiss for one edit.

  // An in-flight save must not leave a row sitting open over a disabled input.
  // Derived rather than cleared from an effect: there is then no second copy of
  // "which row is open" that can disagree with whether a save is running.
  const saving = !!correction?.saving

  return (
    <>
      <div className="rl-details">
        {rows.map((row) => (
          <DetailRow
            key={row.label}
            row={row}
            correction={correction}
            active={activeLabel === row.label && !saving}
            onActivate={() => setActiveLabel(row.label)}
            onDeactivate={() => setActiveLabel(null)}
          />
        ))}
      </div>

      {/* No action row here. The commit lives in the drawer's FOOTER, one
          primary button for the whole pane — staff read down the card, the
          rows, then the one thing to press. A Save button parked mid-pane
          competed with the footer's own primary for the same decision, which
          is the "three ways to do one thing" this ticket exists to end.
          The notice and the error travel with the button for the same reason:
          a message about a press belongs next to what was pressed. */}
    </>
  )
}
